import React from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './SupportDashboard.css';

const SystemDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: stats, isLoading, error } = useQuery(
    'system-dashboard-stats',
    async () => {
      try {
        const response = await api.get('/dashboard/system-stats');
        return response.data;
      } catch (err) {
        console.error('Error al cargar estadísticas del sistema:', err);
        throw err;
      }
    },
    {
      retry: 1,
      enabled: !!user?.is_superuser, // Solo ejecutar si es superusuario
    }
  );

  // Verificar que sea superusuario
  if (!user?.is_superuser) {
    return (
      <div className="error-container">
        <div className="error">
          <h3>❌ Acceso Denegado</h3>
          <p>No tienes permisos para acceder a esta sección.</p>
          <button className="btn btn-primary" onClick={() => navigate('/')}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <div className="loading">Cargando estadísticas del sistema...</div>;
  }

  if (error) {
    const errorMessage = error.response?.data?.detail || 
                        (error.code === 'ECONNABORTED' || error.message === 'Network Error' 
                          ? 'No se pudo conectar con el servidor.'
                          : error.message || 'Error desconocido');
    
    return (
      <div className="error-container">
        <div className="error">
          <h3>❌ Error al cargar estadísticas</h3>
          <p>{errorMessage}</p>
          <button 
            className="btn btn-primary" 
            onClick={() => window.location.reload()}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  // Datos para gráficos
  const tenantsData = stats ? [
    { name: 'Activos', value: stats.active_tenants, color: '#27ae60' },
    { name: 'Inactivos', value: stats.inactive_tenants, color: '#e74c3c' },
  ].filter(item => item.value > 0) : [];

  const usersByRoleData = stats?.users_by_role ? 
    Object.entries(stats.users_by_role).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value: value,
      color: '#3498db'
    })) : [];

  return (
    <div className="support-dashboard">
      <div className="page-header">
        <h2>🔧 Dashboard de Gestión del Sistema</h2>
        <p className="page-subtitle">Vista de administración del sistema - Solo superusuarios</p>
      </div>

      {/* Estadísticas principales del sistema */}
      <div className="stats-grid">
        <div className="stat-card system-card">
          <div className="stat-icon" style={{ backgroundColor: '#3498db20', color: '#3498db' }}>🏢</div>
          <div className="stat-info">
            <h3>Total Organizaciones</h3>
            <p className="stat-number">{stats?.total_tenants || 0}</p>
            <p className="stat-detail">
              {stats?.active_tenants || 0} activos • {stats?.inactive_tenants || 0} inactivos
            </p>
          </div>
        </div>

        <div className="stat-card system-card">
          <div className="stat-icon" style={{ backgroundColor: '#9b59b620', color: '#9b59b6' }}>👤</div>
          <div className="stat-info">
            <h3>Total Usuarios</h3>
            <p className="stat-number">{stats?.total_users || 0}</p>
            <p className="stat-detail">
              {stats?.active_users || 0} activos • {stats?.inactive_users || 0} inactivos
            </p>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="dashboard-content">
        <div className="charts-grid">
          {/* Gráfico de distribución de tenants */}
          <div className="chart-card">
            <h3>Distribución de Organizaciones</h3>
            {tenantsData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={tenantsData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {tenantsData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data">No hay datos de organizaciones</div>
            )}
          </div>

          {/* Gráfico de usuarios por rol */}
          <div className="chart-card">
            <h3>Usuarios por Rol</h3>
            {usersByRoleData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={usersByRoleData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#3498db" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data">No hay datos de usuarios</div>
            )}
          </div>
        </div>

        {/* Alertas del sistema */}
        {stats?.system_alerts && stats.system_alerts.length > 0 && (
          <div className="alerts-section">
            <h3>⚠️ Alertas del Sistema</h3>
            <div className="alerts-grid">
              {stats.system_alerts.map((alert, index) => (
                <div key={index} className={`alert-card alert-${alert.type}`}>
                  <span className="alert-icon">{alert.icon}</span>
                  <span className="alert-message">{alert.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Resumen de Organizaciones */}
        {stats?.tenants_summary && stats.tenants_summary.length > 0 && (
          <div className="tenants-section">
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3>🏢 Organizaciones del Sistema</h3>
                <button className="btn btn-secondary btn-sm" onClick={() => navigate('/tenants')}>
                  Ver todos
                </button>
              </div>
              <div className="tenants-list">
                {stats.tenants_summary.map((tenant) => (
                  <div 
                    key={tenant.id} 
                    className="tenant-item"
                    onClick={() => navigate(`/tenants/${tenant.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="tenant-icon" style={{ 
                      backgroundColor: tenant.is_active ? '#27ae6020' : '#e74c3c20', 
                      color: tenant.is_active ? '#27ae60' : '#e74c3c' 
                    }}>
                      🏢
                    </div>
                    <div className="tenant-info">
                      <h4>{tenant.name} ({tenant.code})</h4>
                      <p className="tenant-detail">
                        {tenant.students_count} estudiantes • {tenant.users_count} usuarios • {tenant.enrollments_count} matrículas
                      </p>
                      <p className="tenant-status">
                        Estado: <span className={`status-badge status-${tenant.is_active ? 'active' : 'inactive'}`}>
                          {tenant.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Acciones de soporte */}
        <div className="info-section">
          <div className="card">
            <h2>Acciones de Gestión del Sistema</h2>
            <p>Gestión de organizaciones y usuarios del sistema</p>
            <div className="quick-actions">
              <button className="btn btn-primary" onClick={() => navigate('/tenants')}>
                Gestionar Organizaciones
              </button>
              <button className="btn btn-secondary" onClick={() => navigate('/users')}>
                Gestionar Usuarios
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemDashboard;

